import type { ThreadItem, VoiceChatMessage } from "./voice-chat-groups.js";

export type PeerReceiptGroup<T extends VoiceChatMessage = VoiceChatMessage> = {
  kind: "peerReceipts";
  key: string;
  messages: T[];
};
export type ConversationItem<T extends VoiceChatMessage = VoiceChatMessage> =
  | ThreadItem<T>
  | PeerReceiptGroup<T>;

/** Direct peer receipts may include their files; human text, input and actions break a run.
 * Прямі квитанції можуть містити свої файли; людський текст, введення й дії переривають групу. */
export function isPurePeerReceipt(message: Pick<VoiceChatMessage, "blocks">): boolean {
  return (
    message.blocks.some(
      (block) => block.kind === "bot_message_sent" || block.kind === "bot_message_received",
    ) &&
    message.blocks.every(
      (block) =>
        block.kind === "bot_message_sent" ||
        block.kind === "bot_message_received" ||
        block.kind === "file" ||
        block.kind === "image",
    )
  );
}

/** Group three markers or any peer run with files; preserve order and unread/call boundaries.
 * Групуються три квитанції або обмін із файлами; порядок і межі непрочитаного/дзвінків збережені. */
export function groupPeerReceipts<T extends VoiceChatMessage>(
  items: readonly ThreadItem<T>[],
  breakBefore: ReadonlySet<string> = new Set(),
): ConversationItem<T>[] {
  const result: ConversationItem<T>[] = [];
  let run: T[] = [];
  const flush = () => {
    if (
      run.length > 2 ||
      run.some((message) =>
        message.blocks.some((block) => block.kind === "file" || block.kind === "image"),
      )
    )
      result.push({ kind: "peerReceipts", key: `peers:${run[0]!.id}`, messages: run });
    else result.push(...run.map((message) => ({ kind: "message" as const, message })));
    run = [];
  };
  for (const item of items) {
    if (item.kind === "message" && isPurePeerReceipt(item.message)) {
      if (breakBefore.has(item.message.id)) flush();
      run.push(item.message);
    } else {
      flush();
      result.push(item);
    }
  }
  flush();
  return result;
}

/** Keep expanded receipts as individual list rows so native lists retain virtualization.
 * Розгорнуті квитанції лишаються окремими рядками для віртуалізації нативного списку. */
export function expandPeerReceiptRows<T extends VoiceChatMessage>(
  items: readonly ConversationItem<T>[],
  expandedMessageIds: ReadonlySet<string>,
): ConversationItem<T>[] {
  return items.flatMap((item): ConversationItem<T>[] =>
    item.kind === "peerReceipts" &&
    item.messages.some((message) => expandedMessageIds.has(message.id))
      ? [item, ...item.messages.map((message) => ({ kind: "message" as const, message }))]
      : [item],
  );
}
